/**
 * User-facing copy for HTTP failures.
 * Gateway pages (nginx 502 HTML) are never shown.
 */

const NETWORK = 'Network error. Check your connection and try again.';
const UNAVAILABLE = 'The service is temporarily unavailable. Please try again in a moment.';
const SERVER = 'Server error. Please try again later.';

/** True when the payload is an HTML error page rather than an API message. */
export function looksLikeGatewayPage(value: string): boolean {
	const text = value.trim();
	return /<!doctype\s+html|<html[\s>]|<head[\s>]|<body[\s>]|<\s*center[\s>]/i.test(text);
}

/** A short API message. HTML pages and blank text are ignored. */
export function safeApiText(value: unknown): string | null {
	if (typeof value !== 'string') return null;
	const text = value.trim();
	if (!text || looksLikeGatewayPage(text) || text.length > 400) return null;
	return text;
}

/**
 * Message for a failed HTTP call.
 * 502, 503, and 504 are proxy failures: the upstream body is not shown.
 */
export function httpErrorMessage(status: number, body: unknown, statusText?: string): string {
	if (status === 0) return NETWORK;
	if (status === 502 || status === 503 || status === 504) return UNAVAILABLE;

	const fromBody = messageFromBody(body);
	if (fromBody) return fromBody;

	if (status >= 500) return SERVER;
	if (status === 403) return 'You do not have permission to perform this action.';
	if (status === 404) return 'The requested resource was not found.';
	if (status === 408) return 'The request timed out. Please try again.';
	if (status === 429) return 'Too many requests. Please wait a moment and try again.';

	const text = safeApiText(statusText);
	if (text) return `${text} (${status})`;
	return status ? `Request failed (${status})` : 'Something went wrong. Please try again.';
}

function messageFromBody(body: unknown): string | null {
	const direct = safeApiText(body);
	if (direct) return direct;
	if (!body || typeof body !== 'object') return null;
	const record = body as Record<string, unknown>;
	return (
		safeApiText(record['message']) ??
		safeApiText(record['detail']) ??
		safeApiText(record['title'])
	);
}
